import { Injectable } from '@angular/core';

import { TranslateService } from '@ngx-translate/core';
import { IdNameMixin, KPI } from '../api/models';
import { ErrorService } from './error.service';

/**
 * Used to get and to format an investment history
 */
@Injectable()
export abstract class HistoryService {

  protected translationMap: { [key: string]: string };
  protected undisplayedObject: string[];
  protected undisplayedFields: string[];

  private trueString: string;
  private falseString: string;
  private unknownValue: string;

  constructor(
    protected errorService: ErrorService,
    protected translateService: TranslateService,
  ) {
    this.trueString = this.translateService.instant('values.true');
    this.falseString = this.translateService.instant('values.false');
    this.unknownValue = this.translateService.instant('values.unknown_value');
  }

  /**
   * If the change is not clear for the user we recover some information that we will display
   *
   * @param change
   */
  protected abstract manageSpecialField(change: KPI.ChangeItem): KPI.ChangeItem;


  /**
   * Transforms the history to display information easily comprehensible by the user
   *
   * @param history
   */
  protected cleanHistory(history: KPI.History): KPI.History {
    const cleanedHistory: KPI.History = {
      history_items: [],
    };

    let cleanHistoryItem: KPI.HistoryItem;

    for (const historyItem of history.history_items) {
      cleanHistoryItem = historyItem;
      const changes: KPI.ChangeItem[] = [];
      let newChange: KPI.ChangeItem;

      for (const change of historyItem.changes) {

        if (!change.object_type && change.field_name === 'id') {
          historyItem.isCreation = true;
        }

        newChange = this.manageSpecialField(change);
        if (this.getFieldVisibility(change.field_name, change.object_type)) {
          newChange.object_type = this.getTranslationKeyFromFieldName(change.object_type);
          newChange.field_name = this.getTranslationKeyFromFieldName(change.field_name);
          changes.push(newChange);
        }
      }

      cleanHistoryItem.changes = changes;
      cleanedHistory.history_items.push(cleanHistoryItem);
    }

    return cleanedHistory;
  }

  /**
   * Returns the translation key for the given field name
   *
   * @param fieldName a field name
   */
  protected getTranslationKeyFromFieldName(fieldName: string): string {
    let result = fieldName;
    if (fieldName in this.translationMap) {
      result = this.translationMap[fieldName];
    } else if (fieldName) {
      /* this.errorService.handleError(
        new Error(`Missing translation key in translationMap: ${fieldName}`),
        false,
      ); */
      // console.info(`Missing translation key in translationMap: ${fieldName}`);
    }
    return result;
  }

  /**
   * Return the visibility of the field (using the field name and the associated object type)
   *
   * @param fieldName a field name
   * @param objectType the type object
   */
  protected getFieldVisibility(fieldName: string, objectType: string): boolean {
    return !this.undisplayedFields.includes(fieldName) && !this.undisplayedObject.includes(objectType);
  }

  /**
   * Recover the name of the object if it exists,
   * else return generic message for unknown object
   *
   * @param object
   */
  protected getName(object: IdNameMixin) {
    return object ? object.name : this.unknownValue;
  }

  /**
   * Convert a boolean to a translation key
   *
   * @param property
   */
  protected boolean2String(bool: boolean): string {
    return bool ? this.trueString : this.falseString;
  }
}
